import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';
import WaFloat from '../../components/WaFloat/WaFloat.jsx';
import { useReveal } from '../../hooks/useReveal.js';

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

const PER_PAGE = 4;

export default function Insights() {
  useReveal();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!loading) {
      const els = document.querySelectorAll('.insights-section .reveal');
      const obs = new IntersectionObserver(
        entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } }),
        { threshold: 0.06 }
      );
      els.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) el.classList.add('visible');
        else obs.observe(el);
      });
      return () => obs.disconnect();
    }
  }, [loading]);

  useEffect(() => {
    axios
      .get('https://taiikitalks.com/api/get-posts/aqt')
      .then((res) => {
        const sorted = [...res.data.data].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setArticles(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Navbar />

      <div className="page-hero about-hero">
        <div className="about-hero-wrap">
          <div className="page-hero-inner">
            <span className="sec-tag reveal">Perspectives</span>
            <h2 className="sec-h reveal" style={{ maxWidth: 560 }}>
              Thinking on capital,<br />growth <em>&amp; complex decisions.</em>
            </h2>
            <p className="sec-sub reveal reveal-delay-1">
              Brief perspectives from the AQT Direct advisory practice. Not commentary. Not news. Considered views on what we observe in the market.
            </p>
          </div>
          <div className="about-hero-img reveal reveal-delay-1">
            <img
              src="https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=800&q=80"
              alt="Financial insights"
            />
          </div>
        </div>
      </div>

      <hr className="rule" />

      <section className="section section-off insights-section">
        {loading ? (
          <div className="insights-loading">Loading insights…</div>
        ) : articles.length === 0 ? (
          <p className="insights-empty">No articles published yet.</p>
        ) : (() => {
          const totalPages = Math.ceil(articles.length / PER_PAGE);
          const visible = articles.slice((page - 1) * PER_PAGE, page * PER_PAGE);
          return (
            <>
              <div className="insights-grid reveal" style={{ marginTop: 0 }}>
                {visible.map((blog) => (
                  <div
                    className="insight-card"
                    key={blog.id}
                    onClick={() => navigate(`/blog-detail/${blog.id}`)}
                  >
                    {blog.category && <span className="insight-tag">{blog.category}</span>}
                    <h3>{blog.title}</h3>
                    {blog.summary && <p>{blog.summary}</p>}
                    <span className="insight-read" style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between' }}>
                      <span>AQT Direct</span>
                      {blog.created_at && <span>{formatDate(blog.created_at)}</span>}
                    </span>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (() => {
                const scroll = () => document.querySelector('.insights-section')?.scrollIntoView({ behavior: 'smooth' });
                const delta = 2;
                const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(n =>
                  n === 1 || n === totalPages || (n >= page - delta && n <= page + delta)
                );
                const withEllipsis = pages.reduce((acc, n, i) => {
                  if (i > 0 && n - pages[i - 1] > 1) acc.push('…');
                  acc.push(n);
                  return acc;
                }, []);
                return (
                  <div className="insights-pagination">
                    <button className="ins-page-btn" disabled={page === 1}
                      onClick={() => { setPage(p => p - 1); scroll(); }}>← Prev</button>
                    {withEllipsis.map((n, i) =>
                      n === '…'
                        ? <span key={`e${i}`} style={{ padding: '8px 4px', color: 'var(--muted)', fontSize: 13 }}>…</span>
                        : <button key={n} className={`ins-page-btn${n === page ? ' active' : ''}`}
                            onClick={() => { setPage(n); scroll(); }}>{n}</button>
                    )}
                    <button className="ins-page-btn" disabled={page === totalPages}
                      onClick={() => { setPage(p => p + 1); scroll(); }}>Next →</button>
                  </div>
                );
              })()}
            </>
          );
        })()}
      </section>

      <div className="cta-strip reveal">
        <h2>Perspectives are only as useful as<br />the <em>conversation they start.</em></h2>
        <p>If any of these observations resonate with your situation, we welcome a private discussion.</p>
        <div className="cta-btns">
          <Link className="btn-white" to="/contactus">Register Interest</Link>
        </div>
      </div>

      <Footer />
      <WaFloat />
    </>
  );
}
